import { Component, OnInit } from '@angular/core';
import {
  RegistrationStepQuery,
  RegistrationStepService,
  MemberDetailsRegistration,
  StepType,
  LoggingService,
  ErrorStateService,
  WizardType,
} from '@fgb/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Regex } from 'src/app/shared/utilities/regex';

@Component({
  selector: 'fgb-registration-personal-details',
  templateUrl: './registration-personal-details.component.html',
  styleUrls: ['./registration-personal-details.component.scss'],
})
export class RegistrationPersonalDetailsComponent implements OnInit {
  readonly stepType = StepType.PersonalDetails;
  readonly wizardTypes = WizardType;
  wizardType?: WizardType;
  hasSubmitted: boolean = false;
  today = new Date();
  loading: boolean = false;
  minAge = new NgbDate(this.today.getFullYear() - 18, this.today.getMonth() + 1, this.today.getDate());

  personalDetailsForm: FormGroup = this.formBuilder.group({
    Title: ['', Validators.required],
    FirstName: ['', Validators.required],
    Surname: ['', Validators.required],
    EmailAddress1: ['', Validators.pattern(Regex.email)],
    DOB: [this.minAge, Validators.required],
    HomeNumber: ['', Validators.required],
    Street: ['', Validators.required],
    Town: ['', Validators.required],
    County: ['', Validators.required],
    PostCode: ['', [Validators.required, Validators.pattern(Regex.postCode)]],
    Country: ['', Validators.required],
    ShouldUseShippingAddress: [false],
  });

  constructor(
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private logService: LoggingService,
    public errorService: ErrorStateService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    const step = this.registrationStepQuery.getStep(this.stepType);
    if (!step) {
      this.logService.warning('No step data found in the store for step', this.stepType);
      return;
    }
    this.wizardType = this.registrationStepQuery.getWizardType();

    this.personalDetailsForm.controls.EmailAddress1.setValue(this.registrationStepQuery.getQueryData('email') || '');
    this._prefillExistingStepData(step.Data as MemberDetailsRegistration);
  }

  submitStep() {
    this.hasSubmitted = true;

    if (!this.personalDetailsForm.valid) {
      this.errorService.clearErrors();

      this.logService.warning(this.personalDetailsForm);
      this.errorService.addError('Some fields are invalid.');
      return;
    }
    this.loading = true;

    let personalDetails = {
      ...this.personalDetailsForm.value,
      DOB: this._getStringFromNgbDate(this.personalDetailsForm.get('DOB')!.value),
    } as MemberDetailsRegistration;

    this._setShippingStepSkipStatus();

    this.registrationStepService
      .submitRegistrationStepData(this.stepType, personalDetails)
      .toPromise()
      .then(() => (this.loading = false));
  }

  /** Prefills the given step data into the form control values. */
  private _prefillExistingStepData(stepData?: MemberDetailsRegistration) {
    if (stepData) {
      for (const [key, value] of Object.entries(stepData)) {
        const control = this.personalDetailsForm.get(key);
        if (control) {
          if (key === 'DOB') {
            let prefilledDob = this._getNgbDateFromString(value);
            if (prefilledDob && new NgbDate(prefilledDob.year, prefilledDob.month, prefilledDob.day).before(this.minAge)) {
              control.setValue(this._getNgbDateFromString(value));
            } else {
              control.setValue(this.minAge as NgbDate);
            }
          } else {
            control.setValue(value);
          }
        }
      }
    }
  }

  /**
   * Set the skip status of the shipping address step based on whether the checkbox was checked on this step or not.
   */
  private _setShippingStepSkipStatus() {
    this.registrationStepService.setStepSkipStatus(
      StepType.ShippingAddress,
      !this.personalDetailsForm.get('ShouldUseShippingAddress')!.value
    );
  }

  /** Gets a date string in ISO format from an NgbDate. */
  private _getStringFromNgbDate(ngbDate: NgbDate) {
    if (ngbDate) {
      let date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, 12);
      date.setUTCHours(0);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);
      return date.toISOString();
    } else {
      return '';
    }
  }

  /** Gets an NgbDate from a date string. */
  private _getNgbDateFromString(dateString: string): NgbDate | null {
    if (dateString) {
      const date = new Date(dateString);
      if (date) {
        return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      }
    }
    return null;
  }
}
